import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchCustomerAssignments, fetchMyCustomerAssignments } from '../../../services/marketing';
import CustomPagination from '../../../components/common/CustomPagination.js';

const CustomerAssignments = ({ scope = 'admin' }) => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    assignment_stage: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { assignment_stage } = filters;
      const params = {
        assignment_stage,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response =
        scope === 'manager'
          ? await fetchMyCustomerAssignments(params)
          : await fetchCustomerAssignments(params);
      
      if (response.results) {
        setAssignments(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setAssignments(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch customer assignments');
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const visibleAssignments = useMemo(() => {
    const term = (filters.search || '').trim().toLowerCase();
    if (!term) return assignments;

    return (assignments || []).filter((assignment) => {
      const searchable = [
        assignment?.customer_username,
        assignment?.customer_email,
        assignment?.manager_name,
        assignment?.manager_employee_id,
        assignment?.assigned_by_username,
        assignment?.assignment_stage,
        assignment?.assignment_method,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [assignments, filters.search]);

  const rowsWithSerial = useMemo(() => {
    return visibleAssignments.map((assignment, index) => ({
      ...assignment,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [visibleAssignments, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'customer_username',
      headerName: 'Customer',
      flex: 1,
      minWidth: 220,
      sortable: false,
      valueGetter: (_, row) => row?.customer_username || '',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.customer_username || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.customer_email || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'manager_name',
      headerName: 'Marketing Manager',
      flex: 1,
      minWidth: 240,
      sortable: false,
      valueGetter: (_, row) => row?.manager_name || '',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.manager_name || 'Unassigned'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.manager_employee_id || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'assignment_stage',
      headerName: 'Stage',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const stage = params.value || 'unassigned';
        const color =
          stage === 'claimed'
            ? 'success'
            : stage === 'unassigned'
              ? 'warning'
              : stage === 'auto_assigned'
                ? 'info'
                : stage === 'admin_assigned'
                  ? 'secondary'
                  : 'default';
        return (
          <Chip
            label={String(stage).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            color={color}
            size="small"
          />
        );
      }
    },
    {
      field: 'assignment_method',
      headerName: 'Assignment Method',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const method = params.value || 'N/A';
        return method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    },
    {
      field: 'assigned_by_username',
      headerName: 'Assigned By',
      width: 160,
      sortable: false,
      valueGetter: (_, row) => row?.assigned_by_username || '',
    },
    {
      field: 'assigned_at',
      headerName: 'Assigned At',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'reassigned_at',
      headerName: 'Reassigned At',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return '-';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return '-';
        }
      }
    },
    {
      field: 'reassignment_count',
      headerName: 'Reassignments',
      width: 130,
      sortable: false,
      renderCell: (params) => params.value || 0
    }
  ], []);

  if (loading && assignments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {scope === 'manager' ? 'My Customer Assignments' : 'Customer Assignments'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('buttons.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Search"
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by customer..."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Stage"
                fullWidth
                value={filters.assignment_stage}
                onChange={(e) => handleFilterChange('assignment_stage', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">All</option>
                <option value="unassigned">Unassigned</option>
                <option value="claimed">Claimed</option>
                <option value="auto_assigned">Auto Assigned</option>
                <option value="admin_assigned">Admin Assigned</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
};

export default CustomerAssignments;
